import type { FloorPlanElement, RadiatorElement, WindowElement, LightElement, SmokeAlarmElement, UtilityElement, DoorElement, BoilerElement, Wall, Room, Point } from '../types';
import { memoizedCalculateRooms } from './performanceOptimizations';
import { comprehensiveSyncWithRedetection, findElementRoomWithConsolidation } from '../services/roomSyncService';

export interface ElementRepairOptions {
  fixMissingWallIds: boolean;
  fixMissingRoomAssignments: boolean;
  fixInvalidPositions: boolean;
  fixMissingProperties: boolean;
  regenerateStableIds: boolean;
  updateElementFormats: boolean;
}

export interface ElementRepairResult {
  elementsRepaired: boolean;
  issuesFound: string[];
  issuesFixed: string[];
  repairedElements: FloorPlanElement[];
  reportSummary: {
    totalElements: number;
    elementsWithIssues: number;
    elementsFixed: number;
    missingWallIdsFixed: number;
    missingRoomAssignmentsFixed: number;
    invalidPositionsFixed: number;
    missingPropertiesFixed: number;
    stableIdsRegenerated: number;
    formatsUpdated: number;
    lightingElementsFixed: number;
    radiatorElementsFixed: number;
    windowElementsFixed: number;
  };
}

export const DEFAULT_ELEMENT_REPAIR_OPTIONS: ElementRepairOptions = {
  fixMissingWallIds: true,
  fixMissingRoomAssignments: true,
  fixInvalidPositions: true,
  fixMissingProperties: true,
  regenerateStableIds: true,
  updateElementFormats: true,
};

/**
 * Main element repair function that fixes recognition issues in old files for lighting, radiators, and windows
 */
export const repairElementRecognition = (
  elements: FloorPlanElement[],
  walls: Wall[],
  roomCustomLabels: Record<string, string> = {},
  currentFloor: number = 1,
  options: ElementRepairOptions = DEFAULT_ELEMENT_REPAIR_OPTIONS
): ElementRepairResult => {
  const result: ElementRepairResult = {
    elementsRepaired: false,
    issuesFound: [],
    issuesFixed: [],
    repairedElements: [],
    reportSummary: {
      totalElements: elements.length,
      elementsWithIssues: 0,
      elementsFixed: 0,
      missingWallIdsFixed: 0,
      missingRoomAssignmentsFixed: 0,
      invalidPositionsFixed: 0,
      missingPropertiesFixed: 0,
      stableIdsRegenerated: 0,
      formatsUpdated: 0,
      lightingElementsFixed: 0,
      radiatorElementsFixed: 0,
      windowElementsFixed: 0,
    }
  };

  let workingElements = [...elements];
  let fixedElementsCount = 0;

  console.log('[ElementRepair] Starting element recognition repair...', {
    totalElements: workingElements.length,
    currentFloor,
    options
  });

  // Calculate rooms for element assignment
  const rooms = memoizedCalculateRooms(walls, roomCustomLabels, 'explicitRoomCreation');

  // Process each element type that commonly has recognition issues
  workingElements = workingElements.map(element => {
    let fixedElement = { ...element };
    let elementFixed = false;
    const elementIssues: string[] = [];
    const elementFixes: string[] = [];

    // Skip elements not on the current floor
    const elementFloor = (element as any).floor;
    if (elementFloor !== undefined && elementFloor !== currentFloor) {
      return element;
    }

    // Fix lighting elements (light, smoke-alarm)
    if (['light', 'smoke-alarm'].includes(element.type)) {
      const lightingRepair = repairLightingElement(element as LightElement | SmokeAlarmElement, rooms, walls, options);
      if (lightingRepair.fixed) {
        fixedElement = lightingRepair.element;
        elementFixed = true;
        elementIssues.push(...lightingRepair.issuesFound);
        elementFixes.push(...lightingRepair.issuesFixed);
        result.reportSummary.lightingElementsFixed++;
      }
    }

    // Fix radiator elements
    if (element.type === 'radiator') {
      const radiatorRepair = repairRadiatorElement(element as RadiatorElement, rooms, walls, options);
      if (radiatorRepair.fixed) {
        fixedElement = radiatorRepair.element;
        elementFixed = true;
        elementIssues.push(...radiatorRepair.issuesFound);
        elementFixes.push(...radiatorRepair.issuesFixed);
        result.reportSummary.radiatorElementsFixed++;
      }
    }

    // Fix window elements
    if (element.type === 'window') {
      const windowRepair = repairWindowElement(element as WindowElement, rooms, walls, options);
      if (windowRepair.fixed) {
        fixedElement = windowRepair.element;
        elementFixed = true;
        elementIssues.push(...windowRepair.issuesFound);
        elementFixes.push(...windowRepair.issuesFixed);
        result.reportSummary.windowElementsFixed++;
      }
    }

    // Fix door elements (also commonly affected)
    if (element.type === 'door') {
      const doorRepair = repairDoorElement(element as DoorElement, rooms, walls, options);
      if (doorRepair.fixed) {
        fixedElement = doorRepair.element;
        elementFixed = true;
        elementIssues.push(...doorRepair.issuesFound);
        elementFixes.push(...doorRepair.issuesFixed);
      }
    }

    // Fix utility elements
    if (element.type === 'utility') {
      const utilityRepair = repairUtilityElement(element as UtilityElement, rooms, walls, options);
      if (utilityRepair.fixed) {
        fixedElement = utilityRepair.element;
        elementFixed = true;
        elementIssues.push(...utilityRepair.issuesFound);
        elementFixes.push(...utilityRepair.issuesFixed);
      }
    }

    // Fix boiler elements
    if (element.type === 'boiler') {
      const boilerRepair = repairBoilerElement(element as BoilerElement, rooms, walls, options);
      if (boilerRepair.fixed) {
        fixedElement = boilerRepair.element;
        elementFixed = true;
        elementIssues.push(...boilerRepair.issuesFound);
        elementFixes.push(...boilerRepair.issuesFixed);
      }
    }

    if (elementFixed) {
      result.reportSummary.elementsWithIssues++;
      result.reportSummary.elementsFixed++;
      result.issuesFound.push(...elementIssues);
      result.issuesFixed.push(...elementFixes);
      fixedElementsCount++;
    }

    return fixedElement;
  });

  result.repairedElements = workingElements;
  result.elementsRepaired = fixedElementsCount > 0;

  console.log(`[ElementRepair] Repair completed:`, {
    totalElements: result.reportSummary.totalElements,
    elementsFixed: result.reportSummary.elementsFixed,
    lightingFixed: result.reportSummary.lightingElementsFixed,
    radiatorsFixed: result.reportSummary.radiatorElementsFixed,
    windowsFixed: result.reportSummary.windowElementsFixed
  });

  return result;
};

/**
 * Repair lighting elements (lights and smoke alarms)
 */
const repairLightingElement = (
  element: LightElement | SmokeAlarmElement,
  rooms: Room[],
  walls: Wall[],
  options: ElementRepairOptions
): { element: LightElement | SmokeAlarmElement; fixed: boolean; issuesFound: string[]; issuesFixed: string[] } => {
  let fixedElement = { ...element } as any;
  let fixed = false;
  const issuesFound: string[] = [];
  const issuesFixed: string[] = [];

  // Check for missing or invalid room assignment
  if (options.fixMissingRoomAssignments && (!fixedElement.roomName || !fixedElement.roomId)) {
    issuesFound.push(`${element.type} missing room assignment`);
    
    const assignedRoom = findElementRoomWithConsolidation(fixedElement.position, rooms, element.type);
    if (assignedRoom) {
      fixedElement.roomName = assignedRoom.name;
      fixedElement.roomId = assignedRoom.id;
      issuesFixed.push(`Assigned ${element.type} to room "${assignedRoom.name}"`);
      fixed = true;
    }
  }

  // Check for invalid position
  if (options.fixInvalidPositions && fixedElement.position) {
    if (!isFinite(fixedElement.position.x) || !isFinite(fixedElement.position.y)) {
      issuesFound.push(`${element.type} has invalid position coordinates`);
      fixedElement.position = { x: 100, y: 100 }; // Reset to safe default
      issuesFixed.push(`Reset ${element.type} position to safe default`);
      fixed = true;
    }
  }

  // Check for missing properties specific to lighting
  if (options.fixMissingProperties) {
    // Ensure floor is set
    if (!fixedElement.floor || fixedElement.floor < 1) {
      issuesFound.push(`${element.type} missing or invalid floor`);
      fixedElement.floor = 1;
      issuesFixed.push(`Set ${element.type} floor to 1`);
      fixed = true;
    }

    // For light elements, ensure light type is set
    if (element.type === 'light' && (!fixedElement.lightType || fixedElement.lightType === '')) {
      issuesFound.push('Light element missing light type');
      fixedElement.lightType = 'low-energy'; // Default to low-energy
      issuesFixed.push('Set light type to low-energy (default)');
      fixed = true;
    }
  }

  // Update element format for new system
  if (options.updateElementFormats) {
    // Ensure ID format is consistent
    if (!fixedElement.id || !fixedElement.id.startsWith(element.type)) {
      const oldId = fixedElement.id;
      fixedElement.id = `${element.type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      issuesFound.push(`${element.type} has inconsistent ID format`);
      issuesFixed.push(`Updated ${element.type} ID from "${oldId}" to "${fixedElement.id}"`);
      fixed = true;
    }
  }

  return { element: fixedElement, fixed, issuesFound, issuesFixed };
};

/**
 * Repair radiator elements
 */
const repairRadiatorElement = (
  element: RadiatorElement,
  rooms: Room[],
  walls: Wall[],
  options: ElementRepairOptions
): { element: RadiatorElement; fixed: boolean; issuesFound: string[]; issuesFixed: string[] } => {
  let fixedElement = { ...element };
  let fixed = false;
  const issuesFound: string[] = [];
  const issuesFixed: string[] = [];

  // Check for missing wall ID
  if (options.fixMissingWallIds && (!fixedElement.wallId || fixedElement.wallId === '')) {
    issuesFound.push('Radiator missing wall ID');
    
    // Find the closest wall to this radiator
    const closestWall = findClosestWallToPoint(fixedElement.position, walls);
    if (closestWall && (closestWall as any).stableId) {
      fixedElement.wallId = (closestWall as any).stableId;
      issuesFixed.push(`Assigned radiator to closest wall ID "${fixedElement.wallId}"`);
      fixed = true;
    }
  }

  // Check for missing room assignment
  if (options.fixMissingRoomAssignments && (!(fixedElement as any).roomName || !(fixedElement as any).roomId)) {
    issuesFound.push('Radiator missing room assignment');
    
    const assignedRoom = findElementRoomWithConsolidation(fixedElement.position, rooms, 'radiator');
    if (assignedRoom) {
      (fixedElement as any).roomName = assignedRoom.name;
      (fixedElement as any).roomId = assignedRoom.id;
      issuesFixed.push(`Assigned radiator to room "${assignedRoom.name}"`);
      fixed = true;
    }
  }

  // Check for missing properties
  if (options.fixMissingProperties) {
    // Ensure radiator type is set
    if (!fixedElement.radiatorType) {
      issuesFound.push('Radiator missing radiator type');
      fixedElement.radiatorType = 'with-trvs'; // Default
      issuesFixed.push('Set radiator type to with-trvs (default)');
      fixed = true;
    }

    // Ensure dimensions are valid
    if (!fixedElement.width || fixedElement.width <= 0) {
      issuesFound.push('Radiator missing or invalid width');
      fixedElement.width = 800; // Default 800mm
      issuesFixed.push('Set radiator width to 800mm (default)');
      fixed = true;
    }

    if (!fixedElement.height || fixedElement.height <= 0) {
      issuesFound.push('Radiator missing or invalid height');
      fixedElement.height = 600; // Default 600mm
      issuesFixed.push('Set radiator height to 600mm (default)');
      fixed = true;
    }

    // Ensure side is set
    if (fixedElement.side === undefined || (fixedElement.side !== -1 && fixedElement.side !== 1)) {
      issuesFound.push('Radiator missing or invalid side');
      fixedElement.side = 1; // Default to positive side
      issuesFixed.push('Set radiator side to 1 (default)');
      fixed = true;
    }
  }

  return { element: fixedElement, fixed, issuesFound, issuesFixed };
};

/**
 * Repair window elements
 */
const repairWindowElement = (
  element: WindowElement,
  rooms: Room[],
  walls: Wall[],
  options: ElementRepairOptions
): { element: WindowElement; fixed: boolean; issuesFound: string[]; issuesFixed: string[] } => {
  let fixedElement = { ...element };
  let fixed = false;
  const issuesFound: string[] = [];
  const issuesFixed: string[] = [];

  // Check for missing wall ID
  if (options.fixMissingWallIds && (!fixedElement.wallId || fixedElement.wallId === '')) {
    issuesFound.push('Window missing wall ID');
    
    const closestWall = findClosestWallToPoint(fixedElement.position, walls);
    if (closestWall && (closestWall as any).stableId) {
      fixedElement.wallId = (closestWall as any).stableId;
      issuesFixed.push(`Assigned window to closest wall ID "${fixedElement.wallId}"`);
      fixed = true;
    }
  }

  // Check for missing room assignment
  if (options.fixMissingRoomAssignments && (!(fixedElement as any).roomName || !(fixedElement as any).roomId)) {
    issuesFound.push('Window missing room assignment');
    
    const assignedRoom = findElementRoomWithConsolidation(fixedElement.position, rooms, 'window');
    if (assignedRoom) {
      (fixedElement as any).roomName = assignedRoom.name;
      (fixedElement as any).roomId = assignedRoom.id;
      issuesFixed.push(`Assigned window to room "${assignedRoom.name}"`);
      fixed = true;
    }
  }

  // Check for missing properties
  if (options.fixMissingProperties) {
    // Ensure dimensions are valid
    if (!fixedElement.width || fixedElement.width <= 0) {
      issuesFound.push('Window missing or invalid width');
      fixedElement.width = 1200; // Default 1200mm
      issuesFixed.push('Set window width to 1200mm (default)');
      fixed = true;
    }

    if (!fixedElement.height || fixedElement.height <= 0) {
      issuesFound.push('Window missing or invalid height');
      fixedElement.height = 1200; // Default 1200mm
      issuesFixed.push('Set window height to 1200mm (default)');
      fixed = true;
    }

    // Ensure trickleVents is boolean
    if (fixedElement.trickleVents === undefined) {
      issuesFound.push('Window missing trickle vents setting');
      fixedElement.trickleVents = 0; // Default to 0
      issuesFixed.push('Set window trickle vents to false (default)');
      fixed = true;
    }
  }

  return { element: fixedElement, fixed, issuesFound, issuesFixed };
};

/**
 * Repair door elements
 */
const repairDoorElement = (
  element: DoorElement,
  rooms: Room[],
  walls: Wall[],
  options: ElementRepairOptions
): { element: DoorElement; fixed: boolean; issuesFound: string[]; issuesFixed: string[] } => {
  let fixedElement = { ...element };
  let fixed = false;
  const issuesFound: string[] = [];
  const issuesFixed: string[] = [];

  // Check for missing wall ID
  if (options.fixMissingWallIds && (!fixedElement.wallId || fixedElement.wallId === '')) {
    issuesFound.push('Door missing wall ID');
    
    const closestWall = findClosestWallToPoint(fixedElement.position, walls);
    if (closestWall && (closestWall as any).stableId) {
      fixedElement.wallId = (closestWall as any).stableId;
      issuesFixed.push(`Assigned door to closest wall ID "${fixedElement.wallId}"`);
      fixed = true;
    }
  }

  // Check for missing properties
  if (options.fixMissingProperties) {
    // Ensure dimensions are valid
    if (!fixedElement.width || fixedElement.width <= 0) {
      issuesFound.push('Door missing or invalid width');
      fixedElement.width = 800; // Default 800mm
      issuesFixed.push('Set door width to 800mm (default)');
      fixed = true;
    }

    // Ensure undercut is boolean
    if (fixedElement.hasUndercut === undefined) {
      issuesFound.push('Door missing undercut setting');
      fixedElement.hasUndercut = false; // Default to false
      issuesFixed.push('Set door undercut to false (default)');
      fixed = true;
    }
  }

  return { element: fixedElement, fixed, issuesFound, issuesFixed };
};

/**
 * Repair utility elements
 */
const repairUtilityElement = (
  element: UtilityElement,
  rooms: Room[],
  walls: Wall[],
  options: ElementRepairOptions
): { element: UtilityElement; fixed: boolean; issuesFound: string[]; issuesFixed: string[] } => {
  let fixedElement = { ...element };
  let fixed = false;
  const issuesFound: string[] = [];
  const issuesFixed: string[] = [];

  // Check for missing room assignment
  if (options.fixMissingRoomAssignments && (!(fixedElement as any).roomName || !(fixedElement as any).roomId)) {
    issuesFound.push('Utility element missing room assignment');
    
    const assignedRoom = findElementRoomWithConsolidation(fixedElement.position, rooms, 'utility');
    if (assignedRoom) {
      (fixedElement as any).roomName = assignedRoom.name;
      (fixedElement as any).roomId = assignedRoom.id;
      issuesFixed.push(`Assigned utility element to room "${assignedRoom.name}"`);
      fixed = true;
    }
  }

  // Check for missing properties
  if (options.fixMissingProperties) {
    // Ensure utility type is set
    if (!fixedElement.utilityType) {
      issuesFound.push('Utility element missing utility type');
      fixedElement.utilityType = 'gas-meter'; // Default
      issuesFixed.push('Set utility type to gas-meter (default)');
      fixed = true;
    }
  }

  return { element: fixedElement, fixed, issuesFound, issuesFixed };
};

/**
 * Repair boiler elements
 */
const repairBoilerElement = (
  element: BoilerElement,
  rooms: Room[],
  walls: Wall[],
  options: ElementRepairOptions
): { element: BoilerElement; fixed: boolean; issuesFound: string[]; issuesFixed: string[] } => {
  let fixedElement = { ...element };
  let fixed = false;
  const issuesFound: string[] = [];
  const issuesFixed: string[] = [];

  // Check for missing wall ID
  if (options.fixMissingWallIds && (!fixedElement.wallId || fixedElement.wallId === '')) {
    issuesFound.push('Boiler missing wall ID');
    
    const closestWall = findClosestWallToPoint(fixedElement.position, walls);
    if (closestWall && (closestWall as any).stableId) {
      fixedElement.wallId = (closestWall as any).stableId;
      issuesFixed.push(`Assigned boiler to closest wall ID "${fixedElement.wallId}"`);
      fixed = true;
    }
  }

  // Check for missing room assignment
  if (options.fixMissingRoomAssignments && (!(fixedElement as any).roomName || !(fixedElement as any).roomId)) {
    issuesFound.push('Boiler missing room assignment');
    
    const assignedRoom = findElementRoomWithConsolidation(fixedElement.position, rooms, 'boiler');
    if (assignedRoom) {
      (fixedElement as any).roomName = assignedRoom.name;
      (fixedElement as any).roomId = assignedRoom.id;
      issuesFixed.push(`Assigned boiler to room "${assignedRoom.name}"`);
      fixed = true;
    }
  }

  // Check for missing properties
  if (options.fixMissingProperties) {
    // Ensure heating type is set
    if (!fixedElement.heatingType) {
      issuesFound.push('Boiler missing heating type');
       fixedElement.heatingType = 'combi'; // Default to a valid HeatingType
      issuesFixed.push('Set boiler heating type to combi (default)');
      fixed = true;
    }
  }

  return { element: fixedElement, fixed, issuesFound, issuesFixed };
};

/**
 * Find the closest wall to a given point
 */
const findClosestWallToPoint = (point: Point, walls: Wall[]): Wall | null => {
  let closestWall: Wall | null = null;
  let closestDistance = Infinity;

  for (const wall of walls) {
    const distance = distanceToLineSegment(point, wall.start, wall.end);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestWall = wall;
    }
  }

  return closestWall;
};

/**
 * Calculate distance from a point to a line segment
 */
const distanceToLineSegment = (point: Point, lineStart: Point, lineEnd: Point): number => {
  const A = point.x - lineStart.x;
  const B = point.y - lineStart.y;
  const C = lineEnd.x - lineStart.x;
  const D = lineEnd.y - lineStart.y;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  
  if (lenSq === 0) {
    // Line segment is actually a point
    return Math.sqrt(A * A + B * B);
  }

  let param = dot / lenSq;
  param = Math.max(0, Math.min(1, param));

  const xx = lineStart.x + param * C;
  const yy = lineStart.y + param * D;

  const dx = point.x - xx;
  const dy = point.y - yy;
  
  return Math.sqrt(dx * dx + dy * dy);
}; 